// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Md5 } from 'ts-md5';
import { SubscribeType } from '@shared/utils/Enums/Constants';
import { NewsSubscribeMessage } from '@shared/vendors/PFSVendor/WebFix/Messages/NewsSubscribeMessage';
import { ApplicationInfo } from '../ApplicationInfo';
import { Connection } from '../Connection';
import { ErrorInformationStorage } from '../ErrorInformationStorage';
import { News } from './News';
import { LocalStorage } from '../LocalStorage';
export class NewsCache {
    constructor(dc) {
        this.onNewsAddEvent = null;
        this.subscribeCache = new Map();
        this.newsRoutes = new Map();
        this.dataCache = dc;
    }
    get NewsRoutes() {
        return new Map(this.newsRoutes);
    }
    addListener(listener) {
        for (const [routeId, route] of this.newsRoutes) {
            this.addListenerInternal(listener, route, routeId);
        }
    }
    removeListener(listener) {
        for (const [routeId, route] of this.newsRoutes) {
            this.removeListenerInternal(listener, route, routeId);
        }
    }
    addRoute(route) {
        var _a;
        if (route.IsNewsRoute && !this.newsRoutes.has(route.RouteId)) {
            this.newsRoutes.set(route.RouteId, (_a = route.Name) !== null && _a !== void 0 ? _a : '');
        }
    }
    addNewsByMessage(message) {
        var _a;
        if (((_a = message === null || message === void 0 ? void 0 : message.NewsGroupsArr) === null || _a === void 0 ? void 0 : _a.length) <= 0)
            return;
        for (const newsGroup of message.NewsGroupsArr) {
            const news = new News(newsGroup);
            news.UpdateByMsg(newsGroup);
            this.onAddNews(news);
        }
    }
    static onNewsReaded(news) {
        if (isNullOrUndefined(news))
            return;
        const newsId = news.GetID();
        if (isNullOrUndefined(newsId))
            return;
        const key = this.getConnectionKey();
        let set = this.readedNewsByConnectionAndLogin.get(key);
        if (!set) {
            set = new Set();
            this.readedNewsByConnectionAndLogin.set(key, set);
        }
        if (set.has(newsId))
            return;
        set.add(newsId);
        if (ApplicationInfo.isExploreMode)
            return;
        this.persistReadNewsToStorage(key, newsId);
    }
    static isNewsReaded(news) {
        if (isNullOrUndefined(news))
            return false;
        const newsId = news.GetID();
        if (isNullOrUndefined(newsId))
            return false;
        const key = this.getConnectionKey();
        const set = this.readedNewsByConnectionAndLogin.get(key);
        if (!isNullOrUndefined(set) && (set === null || set === void 0 ? void 0 : set.has(newsId))) {
            return true;
        }
        return this.isReadNewsInStorage(key, newsId);
    }
    requestNews(startTime, endTime, routeId, maxCountPerRoute, signal) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const routes = routeId === NewsCache.ROUTE_ALL_ID ? Array.from(this.newsRoutes.keys()) : [routeId];
            const result = [];
            for (const rId of routes) {
                if (signal.aborted)
                    return [];
                const msg = yield (Connection === null || Connection === void 0 ? void 0 : Connection.vendor.SendNewsRequestMessage(rId, maxCountPerRoute, startTime, endTime));
                this.processNewsMessage(msg, false);
                if (((_a = msg === null || msg === void 0 ? void 0 : msg.NewsGroupsArr) === null || _a === void 0 ? void 0 : _a.length) <= 0)
                    continue;
                for (const newsGroup of msg.NewsGroupsArr) {
                    if (signal.aborted)
                        return [];
                    if (isNullOrUndefined(newsGroup))
                        continue;
                    const news = new News(newsGroup);
                    news.UpdateByMsg(newsGroup);
                    result.push(news);
                }
            }
            return result;
        });
    }
    processNewsMessage(msg, skipEvent = false) {
        var _a;
        if (((_a = msg === null || msg === void 0 ? void 0 : msg.NewsGroupsArr) === null || _a === void 0 ? void 0 : _a.length) <= 0)
            return;
        for (const newsGroup of msg.NewsGroupsArr) {
            const news = new News(newsGroup);
            if (!skipEvent) {
                this.onAddNews(news);
            }
        }
    }
    clear() {
        this.subscribeCache.clear();
        this.newsRoutes.clear();
        NewsCache.readedNewsByConnectionAndLogin.clear();
    }
    dispose() {
        this.clear();
        this.dataCache = null;
    }
    onAddNews(news) {
        var _a, _b;
        if (!((_a = this.dataCache) === null || _a === void 0 ? void 0 : _a.Loaded))
            return;
        const routeName = this.getNewsRouteName(news.GetRouteID());
        const listeners = this.subscribeCache.get(routeName);
        if (listeners === undefined)
            return;
        listeners.forEach((listener) => listener.onAddNews(news));
        (_b = this.onNewsAddEvent) === null || _b === void 0 ? void 0 : _b.call(this, news);
    }
    getNewsRouteName(routeId) {
        var _a, _b;
        if (routeId === null)
            return '';
        const route = (_a = this.dataCache) === null || _a === void 0 ? void 0 : _a.Routes.getRouteById(routeId);
        return (_b = route === null || route === void 0 ? void 0 : route.Name) !== null && _b !== void 0 ? _b : '';
    }
    addListenerInternal(listener, route, routeId) {
        let listeners = this.subscribeCache.get(route);
        if (listeners === undefined) {
            listeners = new Set();
            this.subscribeCache.set(route, listeners);
            this.subscribeAction(routeId, true, route);
        }
        listeners.add(listener);
    }
    removeListenerInternal(listener, route, routeId) {
        const listeners = this.subscribeCache.get(route);
        if (listeners === undefined)
            return;
        listeners.delete(listener);
        if (listeners.size === 0) {
            this.subscribeCache.delete(route);
            this.subscribeAction(routeId, false, route);
        }
    }
    subscribeAction(routeId, isSubscribe, routeName) {
        try {
            if (routeId === null)
                return;
            const msg = new NewsSubscribeMessage();
            msg.setSubscriptionAction(isSubscribe ? SubscribeType.SUBSCRIBE : SubscribeType.UNSUBSCRIBE);
            msg.addRouteId(routeId);
            Connection === null || Connection === void 0 ? void 0 : Connection.vendor.SendNewsSubscribeMessage(msg);
        }
        catch (_a) {
            ErrorInformationStorage.GetException(`Failed to send subscribe message for news. Route: ${routeName}, isSubscribe: ${isSubscribe}`);
        }
    }
    static getNewsStorageKey() {
        var _a, _b;
        const connectionNameEncrypt = Md5.hashStr((_a = LocalStorage.getLastConnectionName()) !== null && _a !== void 0 ? _a : '');
        const userNameEncrypt = Md5.hashStr((_b = Connection === null || Connection === void 0 ? void 0 : Connection.vendor.GetLogin()) !== null && _b !== void 0 ? _b : '');
        return Md5.hashStr(userNameEncrypt + connectionNameEncrypt);
    }
    static getConnectionKey() {
        return this.getNewsStorageKey();
    }
    markNewsAsRead(news) {
        NewsCache.onNewsReaded(news);
    }
    static persistReadNewsToStorage(key, newsId) {
        const raw = LocalStorage.getViewedNews();
        const data = isNullOrUndefined(raw) ? {} : JSON.parse(raw);
        if (!Array.isArray(data[key])) {
            data[key] = [];
        }
        if (data[key].includes(newsId) === false) {
            data[key].push(newsId);
            LocalStorage.setViewedNews(JSON.stringify(data));
        }
    }
    static isReadNewsInStorage(key, newsId) {
        const raw = LocalStorage.getViewedNews();
        if (isNullOrUndefined(raw))
            return false;
        const data = JSON.parse(raw);
        return Array.isArray(data[key]) && data[key].includes(newsId);
    }
}
NewsCache.ROUTE_ALL_ID = -1;
NewsCache.readedNewsByConnectionAndLogin = new Map();
